import dateFormat, { i18n } from "dateformat";
import {isInVerificationProcess, MemberProductStatus, PeriodicProductPayment} from "./models/periodic-product-payment";
import { PeriodicProduct } from "./models/periodic-product";
import {
  PAYMENT_VALIDATION_STATUS_ERROR,
  PAYMENT_VALIDATION_STATUS_PENDING,
  PAYMENT_VALIDATION_STATUS_VALIDATION
} from "./constants";

// DATES

i18n.dayNames = [
  "Dom.",
  "Lun.",
  "Mar.",
  "Miér.",
  "Juev.",
  "Vier.",
  "Sáb.",
  "Domingo",
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
];

i18n.monthNames = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const months = [ "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre" ];

export const getDate = (date: Date | undefined, format: string, utc: boolean = false) => {
  if (!date) {
    return null;
  }

  return dateFormat(date, format, utc);
}

export const openLinkInNewTab = (url: string) => {
  window.open(url, "_blank", "noreferrer");
};

export const badgeColor = (periodicProductPayment: MemberProductStatus): string => {
  if (periodicProductPayment.isCompleted) return 'green';

  if (isInVerificationProcess(periodicProductPayment)) return 'yellow';

  return (periodicProductPayment.isPastDue || periodicProductPayment.manualCheckStatusId === PAYMENT_VALIDATION_STATUS_ERROR)
    ? 'red'
    : 'blue'
}

export const getBadgeLabel = (periodicProductPayment: MemberProductStatus, completedLabel: string = 'Abonada'): string => {
  if (periodicProductPayment.isCompleted) return completedLabel;

  if (isInVerificationProcess(periodicProductPayment)) return 'En verificación';
  if (periodicProductPayment.manualCheckStatusId === PAYMENT_VALIDATION_STATUS_ERROR) return 'Verificación errónea'

  return 'Sin abonar'
}

export const PREVIOUS_MONTHS_PAYMENT_REQUIRED_MESSAGE = 'Debe abonar los meses anteriores';
const PREVIOUS_MONTHS_LOOKBACK = 3;

export const getProductMonthYear = (product: PeriodicProduct): { month: number; year: number } => {
  if (product.month != null && product.year != null) {
    return { month: product.month, year: product.year };
  }

  const date = new Date(product.startDate);
  return { month: date.getUTCMonth() + 1, year: date.getUTCFullYear() };
};

const isCurrentOrFutureMonth = (month: number, year: number): boolean => {
  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();

  if (year > currentYear) return true;
  if (year === currentYear && month >= currentMonth) return true;
  return false;
};

const getPreviousMonths = (month: number, year: number, count: number): Array<{ month: number; year: number }> => {
  const result: Array<{ month: number; year: number }> = [];
  let currentMonth = month;
  let currentYear = year;

  for (let i = 0; i < count; i++) {
    currentMonth -= 1;
    if (currentMonth < 1) {
      currentMonth = 12;
      currentYear -= 1;
    }
    result.push({ month: currentMonth, year: currentYear });
  }

  return result;
};

const isInMonthYearSet = (
  month: number,
  year: number,
  periods: Array<{ month: number; year: number }>
): boolean => periods.some((period) => period.month === month && period.year === year);

export const arePreviousMonthsSatisfied = (
  product: MemberProductStatus,
  allProducts: MemberProductStatus[],
  toPayList: MemberProductStatus[]
): boolean => {
  const { month: productMonth, year: productYear } = getProductMonthYear(product.product);

  if (!isCurrentOrFutureMonth(productMonth, productYear)) {
    return true;
  }

  const previousSixMonths = getPreviousMonths(productMonth, productYear, PREVIOUS_MONTHS_LOOKBACK);
  const productsInWindow = allProducts.filter((candidate) => {
    const { month, year } = getProductMonthYear(candidate.product);
    return isInMonthYearSet(month, year, previousSixMonths);
  });

  return productsInWindow.every(
    (previousProduct) =>
      previousProduct.isCompleted ||
      toPayList.some((selected) => selected.productId === previousProduct.productId)
  );
};

export const isBlockedByPreviousUnpaidMonths = (
  product: MemberProductStatus,
  allProducts: MemberProductStatus[],
  toPayList: MemberProductStatus[]
): boolean => {
  if (product.isCompleted) return false;

  return !arePreviousMonthsSatisfied(product, allProducts, toPayList);
};

export const paymentCheckboxIsDisabled = (
  periodicProductPayment: MemberProductStatus,
  allProducts: MemberProductStatus[] = [],
  toPayList: MemberProductStatus[] = [],
  isChecked: boolean = false
): boolean => {
  if (periodicProductPayment.isCompleted) return true;

  if (
    periodicProductPayment.manualCheckStatusId !== null &&
    (periodicProductPayment.manualCheckStatusId === PAYMENT_VALIDATION_STATUS_PENDING ||
      periodicProductPayment.manualCheckStatusId === PAYMENT_VALIDATION_STATUS_VALIDATION)
  ) {
    return true;
  }

  if (allProducts.length > 0 && isBlockedByPreviousUnpaidMonths(periodicProductPayment, allProducts, toPayList) && !isChecked) {
    return true;
  }

  return false;
};

export const manualCheckStatusNotPayable = (periodicProductPayment: MemberProductStatus): boolean => periodicProductPayment.manualCheckStatusId !== null && (periodicProductPayment.manualCheckStatusId === PAYMENT_VALIDATION_STATUS_PENDING || periodicProductPayment.manualCheckStatusId === PAYMENT_VALIDATION_STATUS_VALIDATION);
